import { useEffect, useState } from 'react';
import api from '../../api';
import { useTheme } from '../../ThemeProvider';

function ThemeSettings() {
  const { updateTheme } = useTheme();
  const [settings, setSettings] = useState({
    theme_background: '#2d2d2d',
    theme_background_secondary: '#3d3d3d',
    theme_text: '#ffffff',
    theme_text_secondary: '#b0b0b0',
    theme_accent: '#c9a050',
    theme_accent_hover: '#d4b366'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    api.get('/api/admin/settings')
      .then(res => {
        const themeSettings = {};
        Object.keys(res.data).forEach(key => {
          if (key.startsWith('theme_')) {
            themeSettings[key] = res.data[key];
          }
        });
        setSettings(prev => ({ ...prev, ...themeSettings }));
      })
      .catch(err => console.error('Failed to fetch settings:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/api/admin/settings', settings);
      
      const themeData = {
        background: settings.theme_background,
        background_secondary: settings.theme_background_secondary,
        text: settings.theme_text,
        text_secondary: settings.theme_text_secondary,
        accent: settings.theme_accent,
        accent_hover: settings.theme_accent_hover
      };
      updateTheme(themeData);
      
      setMessage({ type: 'success', text: 'Theme settings saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const presets = [
    {
      name: 'Dark Gray (Default)',
      values: {
        theme_background: '#2d2d2d',
        theme_background_secondary: '#3d3d3d',
        theme_text: '#ffffff',
        theme_text_secondary: '#b0b0b0',
        theme_accent: '#c9a050',
        theme_accent_hover: '#d4b366'
      }
    },
    {
      name: 'Deep Navy',
      values: {
        theme_background: '#1a1f2e',
        theme_background_secondary: '#252b3d',
        theme_text: '#ffffff',
        theme_text_secondary: '#a0a8b8',
        theme_accent: '#d4af37',
        theme_accent_hover: '#e5c158'
      }
    },
    {
      name: 'Classic Black',
      values: {
        theme_background: '#0a0a0a',
        theme_background_secondary: '#1a1a1a',
        theme_text: '#ffffff',
        theme_text_secondary: '#888888',
        theme_accent: '#b8860b',
        theme_accent_hover: '#daa520'
      }
    },
    {
      name: 'Charcoal',
      values: {
        theme_background: '#36454f',
        theme_background_secondary: '#465663',
        theme_text: '#f5f5f5',
        theme_text_secondary: '#c0c0c0',
        theme_accent: '#cd853f',
        theme_accent_hover: '#deb887'
      }
    }
  ];

  const applyPreset = (preset) => {
    setSettings(prev => ({ ...prev, ...preset.values }));
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div>
      <div className="admin-header">
        <h1>Theme Settings</h1>
        <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '15px' }}>Quick Presets</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {presets.map(preset => (
            <button 
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className="btn btn-small btn-secondary"
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
        <div style={{ backgroundColor: 'var(--background-secondary)', padding: '20px', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '20px' }}>Background Colors</h3>
          
          <div className="form-group">
            <label>Primary Background</label>
            <div className="color-picker-group">
              <input
                type="color"
                value={settings.theme_background}
                onChange={(e) => handleChange('theme_background', e.target.value)}
              />
              <input
                type="text"
                value={settings.theme_background}
                onChange={(e) => handleChange('theme_background', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Secondary Background</label>
            <div className="color-picker-group">
              <input
                type="color"
                value={settings.theme_background_secondary}
                onChange={(e) => handleChange('theme_background_secondary', e.target.value)}
              />
              <input
                type="text"
                value={settings.theme_background_secondary}
                onChange={(e) => handleChange('theme_background_secondary', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{ backgroundColor: 'var(--background-secondary)', padding: '20px', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '20px' }}>Text Colors</h3>
          
          <div className="form-group">
            <label>Primary Text</label>
            <div className="color-picker-group">
              <input
                type="color"
                value={settings.theme_text}
                onChange={(e) => handleChange('theme_text', e.target.value)}
              />
              <input
                type="text"
                value={settings.theme_text}
                onChange={(e) => handleChange('theme_text', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Secondary Text</label>
            <div className="color-picker-group">
              <input
                type="color"
                value={settings.theme_text_secondary}
                onChange={(e) => handleChange('theme_text_secondary', e.target.value)}
              />
              <input
                type="text"
                value={settings.theme_text_secondary}
                onChange={(e) => handleChange('theme_text_secondary', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{ backgroundColor: 'var(--background-secondary)', padding: '20px', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '20px' }}>Accent Colors</h3>
          
          <div className="form-group">
            <label>Accent Color</label>
            <div className="color-picker-group">
              <input
                type="color"
                value={settings.theme_accent}
                onChange={(e) => handleChange('theme_accent', e.target.value)}
              />
              <input
                type="text"
                value={settings.theme_accent}
                onChange={(e) => handleChange('theme_accent', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Accent Hover</label>
            <div className="color-picker-group">
              <input
                type="color"
                value={settings.theme_accent_hover}
                onChange={(e) => handleChange('theme_accent_hover', e.target.value)}
              />
              <input
                type="text"
                value={settings.theme_accent_hover}
                onChange={(e) => handleChange('theme_accent_hover', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '40px', padding: '20px', backgroundColor: 'var(--background-secondary)', borderRadius: '8px' }}>
        <h3 style={{ marginBottom: '15px' }}>Preview</h3>
        <div style={{ 
          padding: '30px', 
          borderRadius: '8px',
          backgroundColor: settings.theme_background
        }}>
          <p style={{ color: settings.theme_text, marginBottom: '10px' }}>
            This is primary text on the primary background.
          </p>
          <p style={{ color: settings.theme_text_secondary, marginBottom: '15px' }}>
            This is secondary text for descriptions.
          </p>
          <div style={{ 
            padding: '15px', 
            backgroundColor: settings.theme_background_secondary,
            borderRadius: '4px',
            marginBottom: '15px'
          }}>
            <p style={{ color: settings.theme_text }}>Content on secondary background</p>
          </div>
          <button style={{ 
            padding: '10px 20px',
            backgroundColor: settings.theme_accent,
            color: settings.theme_background,
            border: 'none',
            borderRadius: '4px',
            fontWeight: '600'
          }}>
            Accent Button
          </button>
        </div>
      </div>
    </div>
  );
}

export default ThemeSettings;
